"use client"

import { useEffect, useRef } from "react"

interface CustomScriptsClientProps {
  scripts: string[]
}

/**
 * Client-side component to render custom HTML/scripts from database
 * This prevents hydration mismatch since scripts may inject DOM elements dynamically
 */
export default function CustomScriptsClient({
  scripts,
}: CustomScriptsClientProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!containerRef.current || scripts.length === 0) return

    // Inject scripts into the DOM on client side only
    scripts.forEach((html, index) => {
      const wrapper = document.createElement("div")
      wrapper.setAttribute("data-custom-script", `${index}`)
      wrapper.innerHTML = html
      containerRef.current?.appendChild(wrapper)

      // Execute any scripts in the HTML
      const scriptTags = wrapper.getElementsByTagName("script")
      Array.from(scriptTags).forEach((oldScript) => {
        const newScript = document.createElement("script")
        Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value)
        })
        newScript.textContent = oldScript.textContent
        oldScript.parentNode?.replaceChild(newScript, oldScript)
      })
    })

    // Cleanup function
    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = ""
      }
    }
  }, [scripts])

  return <div ref={containerRef} suppressHydrationWarning />
}
